import React, { useEffect, useState } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import { COMPASS_IMG } from "@/lib/api";

const DISMISS_KEY = "compass_install_dismissed_at";
const DISMISS_DAYS = 7;

const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts && Date.now() - ts < DISMISS_DAYS * 86400000;
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari never fires beforeinstallprompt — show manual hint after a beat.
    let t;
    if (isIOS()) t = setTimeout(() => { setIosHint(true); setShow(true); }, 2500);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) {}
    setDeferred(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div data-testid="install-prompt" className="fixed inset-x-0 bottom-0 z-[60] p-4 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-4 animate-fade-up">
        <div className="flex items-start gap-3">
          <img src={COMPASS_IMG} alt="The Daily Compass" className="w-12 h-12 rounded-xl object-cover compass-glow shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-heading font-semibold">Install The Daily Compass</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {iosHint
                ? "Add to your Home Screen for one-tap access."
                : "Get one-tap access, a full-screen app, and faster launches."}
            </p>

            {iosHint ? (
              <p className="mt-3 text-sm text-foreground/90 flex items-center gap-1.5 flex-wrap">
                Tap <Share className="w-4 h-4 inline text-primary" /> then
                <span className="inline-flex items-center gap-1 font-medium">“Add to Home Screen” <Plus className="w-3.5 h-3.5" /></span>
              </p>
            ) : (
              <div className="mt-3 flex items-center gap-2">
                <button data-testid="install-accept" onClick={install}
                  className="flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full hover:bg-atlantic active:scale-95 transition-all">
                  <Download className="w-4 h-4" /> Install
                </button>
                <button data-testid="install-dismiss" onClick={dismiss} className="text-sm text-muted-foreground px-3 py-2 rounded-full hover:bg-secondary">
                  Not now
                </button>
              </div>
            )}
          </div>
          <button data-testid="install-close" onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0"><X className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
